import { createApi } from '@reduxjs/toolkit/query/react';
import config from 'config/index';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';
import { EUserType } from 'types/accessControll.types';
import type { TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import type { TScoringCategory } from 'types/scoringCategory.types';
import type { TJobRole, TSupportedLanguage, TSupportedLanguageCode } from './misc.api.types';
import type { TSmartAck } from 'types/smartAcks.types';

const reducerPath = 'misc' as const;
const path = 'v1/misc' as const;

export const miscApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  endpoints: (builder) => ({
    getSupportedLanguages: builder.query<
      { languages: TSupportedLanguage[] },
      {
        allow?: TAvailableLanguageCodes[];
        exclude?: TAvailableLanguageCodes[];
      }
    >({
      query: (x) => {
        return {
          url: `/get-supported-languages`,
          method: 'POST',
          data: x,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    getSupportedLanguageCodes: builder.query<{ languageCodes: TSupportedLanguageCode[] }, { exclude?: string[] }>({
      query: (data: { exclude: string[] }) => {
        return {
          url: `/get-supported-language-codes`,
          method: 'POST',
          data,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    getSupportedJobRoles: builder.query<{ roles: TJobRole[] }, void>({
      query: () => {
        return {
          url: `/get-supported-job-roles`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    getScoringCategories: builder.query<
      {
        scoringCategories: {
          label: string;
          value: string;
          description: string;
        }[];
      },
      void
    >({
      query: () => {
        return {
          url: `/get-scoring-categories`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    getScoringCategory: builder.query<TScoringCategory, { categoryId: string }>({
      query: (x) => {
        return {
          url: `/scoring-category/${x.categoryId}`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    getSmartAcks: builder.query<{ smartAcks: Pick<TSmartAck, 'name' | 'id' | 'description'>[] }, void>({
      query: () => ({
        url: `/smart-acks`,
        method: 'GET',
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    getDetectionConfusableLanguageCodes: builder.query<{ code: string }[], { data: TAvailableLanguageCodes[] }>({
      query: (payload) => ({
        url: `/get-detection-confusable-language-codes`,
        method: 'POST',
        data: payload,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),
  }),
});

export const {
  // Queries
  useGetSupportedLanguagesQuery,
  useGetSupportedLanguageCodesQuery,
  useGetSupportedJobRolesQuery,
  useGetScoringCategoriesQuery,
  useGetScoringCategoryQuery,
  useGetSmartAcksQuery,
  useGetDetectionConfusableLanguageCodesQuery,
  useLazyGetDetectionConfusableLanguageCodesQuery,
  // Mutations
} = miscApi;
